/**
 * @fileoverview Animated voice waveform component
 *
 * Shows vertical bars that animate when speech is detected,
 * providing visual feedback for voice input activity.
 */

import { useEffect, useState } from 'react';

function VoiceWaveform({ isListening, hasInterimResults, className = "" }) {
  const [isAnimating, setIsAnimating] = useState(false);

  // Control animation based on speech activity
  useEffect(() => {
    if (isListening && hasInterimResults) {
      setIsAnimating(true);
    } else if (!hasInterimResults) {
      // Stop animating immediately when browser detects pause
      setIsAnimating(false);
    }
  }, [isListening, hasInterimResults]);

  // Generate 5 bars with diamond/mountain shape in resting state
  const bars = Array.from({ length: 5 }, (_, index) => {
    // Diamond pattern heights: [6, 10, 14, 10, 6]
    const diamondHeights = [6, 10, 14, 10, 6];
    const baseHeight = diamondHeights[index];
    const maxHeight = 20; // Maximum height when active

    // Create staggered animation delays
    const animationDelay = index * 0.1;

    return (
      <div
        key={index}
        className={`
          bg-current rounded-full transition-all duration-200 ease-in-out
          ${isAnimating ? 'voice-bar-animate' : ''}
        `}
        style={{
          width: '3px',
          height: `${isAnimating ? maxHeight : baseHeight}px`,
          animationDelay: `${animationDelay}s`,
          transform: isAnimating ? 'scaleY(1)' : 'scaleY(1)',
          transformOrigin: 'center'
        }}
      />
    );
  });

  return (
    <>
      {/* CSS animation keyframes */}
      <style>{`
        @keyframes voiceBarPulse {
          0%, 100% { transform: scaleY(0.6); }
          25% { transform: scaleY(1.2); }
          50% { transform: scaleY(0.8); }
          75% { transform: scaleY(1.1); }
        }

        .voice-bar-animate {
          animation: voiceBarPulse 0.8s ease-in-out infinite;
        }

        .voice-bar-animate:nth-child(1) { animation-delay: 0s; }
        .voice-bar-animate:nth-child(2) { animation-delay: 0.1s; }
        .voice-bar-animate:nth-child(3) { animation-delay: 0.2s; }
        .voice-bar-animate:nth-child(4) { animation-delay: 0.3s; }
        .voice-bar-animate:nth-child(5) { animation-delay: 0.4s; }
      `}</style>

      <div className={`flex items-center justify-center space-x-1 h-6 w-6 ${className}`}>
        {bars}
      </div>
    </>
  );
}

export default VoiceWaveform;